import { memo, useState } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useFormContext, useWatch } from 'react-hook-form';
import { Map } from 'features/mapping/components/Map';
import { MapType } from 'features/mapping/mapTypePolicy';
import { useMapState } from 'state/useMapState';
import { LatLng } from 'shared/types';
import { ReportableGeoEvent } from 'features/reports/components/ReportForm/types';
import { DynamicMapCenter } from 'features/mapping/components/DynamicMapCenter';
import {
  isFloodingGeoEvent,
  isHazardGeoEvent,
  isWildfireGeoEvent,
} from 'features/geoEvents/geoEvent.utils';
import { hasValidCoordinates } from 'shared/utils';
import { GeoEventType } from 'features/geoEvents/geoEvent.types';
import { useFormMapGeoEvent } from './useFormMapGeoEvent';

type MediaMapProps = {
  geoEvent?: ReportableGeoEvent;
  geoEventType?: GeoEventType;
  lat: number | null;
  lng: number | null;
  azFieldName: string;
  mapId?: string; // Optional map ID to target a specific map instance on pages with multiple maps
};

const getDefaultMapCenter = (
  coords: { lat: number | null; lng: number | null },
  incidentMap: { center: LatLng },
): LatLng => {
  if (hasValidCoordinates(coords)) {
    return { lat: coords.lat, lng: coords.lng };
  }

  if (hasValidCoordinates(incidentMap.center)) {
    const { lat, lng } = incidentMap.center;
    return { lat, lng };
  }

  return Map.DefaultConfig.center;
};

const useStyles = makeStyles()((theme) => ({
  mapContainer: {
    width: '100%',
    height: 225,
    borderRadius: Number(theme.shape.borderRadius) * 1.34,
    overflow: 'hidden',
  },
}));

export const MediaMap = memo((props: MediaMapProps): JSX.Element => {
  const { geoEvent, geoEventType, lat, lng, azFieldName, mapId } = props;
  const { classes } = useStyles();
  const { incidentMapState } = useMapState();
  const [initialMapCenter] = useState(() =>
    getDefaultMapCenter(props, incidentMapState),
  );
  const { control } = useFormContext();
  const mapGeoEvent = useFormMapGeoEvent({ geoEvent, geoEventType });

  const azimuth: number = useWatch({
    control,
    name: azFieldName,
    defaultValue: 0,
  });

  return (
    <Box className={classes.mapContainer}>
      <Map id={mapId} center={initialMapCenter} mapType={MapType.MediaForm}>
        {/* Do NOT need to pass mapId, since DynamicMapCenter is used within the media map itself */}
        <DynamicMapCenter lat={lat} lng={lng} zoom={9} />

        {isFloodingGeoEvent(mapGeoEvent) && (
          <Map.FloodingGeoEventMarker geoEvent={mapGeoEvent} />
        )}
        {isWildfireGeoEvent(mapGeoEvent) && (
          <Map.WildFireGeoEventMarker geoEvent={mapGeoEvent} />
        )}
        {isHazardGeoEvent(mapGeoEvent) && (
          <Map.HazardGeoEventMarker geoEvent={mapGeoEvent} />
        )}

        {lat && lng && (
          <Map.ReportMarker
            isFadable={false}
            lat={lat}
            lng={lng}
            az={azimuth}
            overrideCoords
          />
        )}
      </Map>
    </Box>
  );
});

MediaMap.displayName = 'MediaMap';
